import React, { FC, useMemo } from "react";
import { Box, MenuItem, TextField, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { HAMASPIK_COLORS, HAMASPIK_CONTROL_HEIGHT, HAMASPIK_FONT_DISPLAY } from "../../../../../assets/hamaspik/theme";
import Badge from "../../../../../assets/hamaspik/Badge";
import { PostIfLookupItemDto, PostIfPositionRulesDto } from "../../../../../types/pifDto";
import { parsePifDate, pifDateFromPicker } from "../../pifDates";
import { pifFieldId } from "../../pifFocus";
import {
  PIF_PAST_START_NOTES_MESSAGE,
  PIF_START_DATE_MAX_YEARS_BACK,
  getPifStartDateMin,
  isCommunityDsp,
  isFullTimeAllowed,
  isPifStartDatePast,
  isPifStartDateTooOld,
} from "../../pifValidation";
import { PifMenuSelect } from "../../pifSelectMenu";
import { PifFormValues, toSelectId } from "../../types";
import PifField from "./PifField";
import PifSectionCard from "./PifSectionCard";

interface PifSectionPositionProps {
  values: PifFormValues;
  onChange: (patch: Partial<PifFormValues>) => void;
  rules: PostIfPositionRulesDto | null;
  departments: PostIfLookupItemDto[];
  positions: PostIfLookupItemDto[];
  reportToUsers: PostIfLookupItemDto[];
  services: PostIfLookupItemDto[];
  clients: PostIfLookupItemDto[];
}

const PifSectionPosition: FC<PifSectionPositionProps> = ({
  values: f,
  onChange,
  rules,
  departments,
  positions,
  reportToUsers,
  services,
  clients,
}) => {
  /**
   * Department gates position, so changing it clears everything scoped beneath it. Not Report To:
   * since Epic 82836 that list is every active user, independent of the department.
   */
  const handleDeptChange = (id: number | null) => {
    onChange({
      deptId: id,
      dept: departments.find((d) => d.id === id)?.text ?? "",
      posId: null,
      pos: "",
      serviceId: null,
      service: "",
      clientId: null,
      client: "",
    });
  };

  const handlePosChange = (id: number | null) => {
    onChange({
      posId: id,
      pos: positions.find((p) => p.id === id)?.text ?? "",
      serviceId: null,
      service: "",
      clientId: null,
      client: "",
    });
  };

  const startDateValue = parsePifDate(f.startDate);
  const startPast = isPifStartDatePast(f.startDate);
  const startTooOld = isPifStartDateTooOld(f.startDate);
  const startDateMin = useMemo(getPifStartDateMin, []);
  // Locked only once the rules are LOADED and actually forbid full-time. `rules === null` means no
  // position has been chosen yet, i.e. unknown — disabling on that locked the toggle with no
  // explanation (the note below has always been guarded on `rules`), and once the employment-history
  // seed started pre-selecting Full-time it rendered that selection in MUI's grey disabled palette,
  // which reads as "nothing is selected". PifWorkspace clamps the value if the position rules out FT.
  const ftLocked = !!rules && !isFullTimeAllowed(rules);
  // Epic 82836: Service applies to a Community-DSP position only, and it is required there - so
  // render and requirement share one gate (getPifGaps) and the gap can never be unfillable. Gated
  // on the rules, not on services.length, which loads independently. An unseeded Role Type has no
  // classification, so it stays hidden.
  const showService = isCommunityDsp(rules);
  const showClient = isCommunityDsp(rules);

  return (
    <PifSectionCard
      id="sec-position"
      title="The position"
      badge={rules?.classification ? <Badge variant="primary" size="sm">{rules.classification}</Badge> : undefined}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        {/* SelectDisplayProps.id, not id: MUI puts `id` on the Select's hidden input, and the
            element a validation error has to focus is the display button. The plain `id` prop is
            right for every TextField / DatePicker below. See pifFocus. */}
        {/* Epic 82836: the first field of the section, listing every active user - no longer gated
            on, or cleared by, the department (that narrowing is PIF-L2 / Epic 67126). */}
        <PifField label="Reports to *">
          <PifMenuSelect
            size="small"
            fullWidth
            displayEmpty
            value={f.reportsToId ?? ""}
            onChange={(e) => {
              const id = toSelectId(e.target.value);
              onChange({ reportsToId: id, reportsTo: reportToUsers.find((u) => u.id === id)?.text ?? "" });
            }}
            SelectDisplayProps={{ id: pifFieldId("reportsToId") }}
          >
            <MenuItem value="">{reportToUsers.length ? "Please select" : "No active users found contact HR"}</MenuItem>
            {/* Same as Service below: a restored UserId that is not in the list has no MenuItem, so
                the Select would render blank and read as unsaved. The option COUNT goes early in
                the short label - MUI truncates the closed Select. A count of 0 means the lookup
                returned nothing; any other count means the stored user is no longer active. */}
            {f.reportsToId !== null && !reportToUsers.some((u) => u.id === f.reportsToId) && (
              <MenuItem value={f.reportsToId} disabled>
                {`id ${f.reportsToId} — ${reportToUsers.length} options — re-pick`}
              </MenuItem>
            )}
            {reportToUsers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.text}
              </MenuItem>
            ))}
          </PifMenuSelect>
        </PifField>
        <PifField label="Department *">
          <PifMenuSelect
            size="small"
            fullWidth
            displayEmpty
            value={f.deptId ?? ""}
            onChange={(e) => handleDeptChange(toSelectId(e.target.value))}
            SelectDisplayProps={{ id: pifFieldId("deptId") }}
          >
            <MenuItem value="">Please select</MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>
                {d.text}
              </MenuItem>
            ))}
          </PifMenuSelect>
        </PifField>
        <PifField label="Position *">
          <PifMenuSelect
            size="small"
            fullWidth
            displayEmpty
            disabled={!f.deptId}
            value={f.posId ?? ""}
            onChange={(e) => handlePosChange(toSelectId(e.target.value))}
            SelectDisplayProps={{ id: pifFieldId("posId") }}
          >
            <MenuItem value="">{f.deptId ? "Please select" : "Pick a department first"}</MenuItem>
            {positions.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.text}
              </MenuItem>
            ))}
          </PifMenuSelect>
        </PifField>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1.75 }}>
        <PifField label="Intended start date *">
          {/* Bug 83249: typed dates. The rest of the portal's pickers stay calendar-only. */}
          <DatePicker
            value={startDateValue}
            minDate={startDateMin}
            onChange={(value) => {
              const next = pifDateFromPicker(value);
              if (next === null) return;
              onChange({ startDate: next });
            }}
            slotProps={{
              textField: { id: pifFieldId("startDate"), size: "small", fullWidth: true },
            }}
          />
          {startTooOld ? (
            <Typography variant="caption" color="error">
              {`Can't be more than ${PIF_START_DATE_MAX_YEARS_BACK} years ago. Pick a later date.`}
            </Typography>
          ) : startPast && (
            <Typography variant="caption" color="warning.main">
              {PIF_PAST_START_NOTES_MESSAGE}
            </Typography>
          )}
        </PifField>
        <PifField label="Post-interview date">
          <TextField size="small" fullWidth disabled value={new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })} />
        </PifField>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }, gap: 1.75, alignItems: "start" }}>
        <PifField label="Employment type *">
          <FtPtToggle id={pifFieldId("empType")} empType={f.empType} disabled={ftLocked} onChange={(v) => onChange({ empType: v })} />
          {ftLocked && (
            <Typography variant="caption" color="text.secondary">
              {isCommunityDsp(rules)
                ? "This position is always part-time."
                : "Not schedule-based, so it can't be classified full-time."}
            </Typography>
          )}
        </PifField>
        {showService && (
          <PifField label="Service *">
            <PifMenuSelect
              size="small"
              fullWidth
              displayEmpty
              value={f.serviceId ?? ""}
              onChange={(e) => {
                const id = toSelectId(e.target.value);
                onChange({
                  serviceId: id,
                  service: services.find((s) => s.id === id)?.text ?? "",
                  clientId: null,
                  client: "",
                });
              }}
              SelectDisplayProps={{ id: pifFieldId("serviceId") }}
            >
              <MenuItem value="">
                {services.length ? "Please select" : "No services set up for this department"}
              </MenuItem>
              {/* A reopened draft can hold a ServiceId this department no longer offers. Without
                  a matching MenuItem, MUI renders the Select BLANK - indistinguishable from "the
                  value never saved" - so surface it and make the re-pick explicit. */}
              {f.serviceId !== null && !services.some((s) => s.id === f.serviceId) && (
                <MenuItem value={f.serviceId} disabled>
                  {`id ${f.serviceId} — ${services.length} options — re-pick`}
                </MenuItem>
              )}
              {services.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.text}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </PifField>
        )}
      </Box>

      {showClient && (
        <Box sx={{ p: 2.125, bgcolor: "rgba(245,127,67,0.06)", border: "1.5px solid", borderColor: "warning.light", borderRadius: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography sx={{ fontFamily: HAMASPIK_FONT_DISPLAY, fontWeight: 800, fontSize: 13.5, color: HAMASPIK_COLORS.heading }}>Client assignment</Typography>
            <Badge variant="primary" size="sm" sx={{ bgcolor: "#fff" }}>COMMUNITY-BASED DSP</Badge>
          </Box>
          <PifField label="Individual / client *">
            <PifMenuSelect
              size="small"
              fullWidth
              displayEmpty
              disabled={!f.serviceId}
              value={f.clientId ?? ""}
              onChange={(e) => {
                const id = toSelectId(e.target.value);
                onChange({ clientId: id, client: clients.find((c) => c.id === id)?.text ?? "" });
              }}
              SelectDisplayProps={{ id: pifFieldId("clientId") }}
            >
              <MenuItem value="">{f.serviceId ? "Please select" : "Pick a service first"}</MenuItem>
              {clients.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.text}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </PifField>
        </Box>
      )}

    </PifSectionCard>
  );
};

const FtPtToggle: FC<{
  /** Focus target for a validation error, on the first of the pair — see pifFocus. */
  id: string;
  empType: "Part-time" | "Full-time";
  disabled: boolean;
  onChange: (v: "Part-time" | "Full-time") => void;
}> = ({ id, empType, disabled, onChange }) => {
  return (
    <Box sx={{ display: "flex", gap: 1.25, "& .MuiButton-root": { minHeight: HAMASPIK_CONTROL_HEIGHT } }}>
      <Button id={id} variant={empType === "Part-time" ? "contained" : "outlined"} size="small" onClick={() => onChange("Part-time")}>
        Part-time
      </Button>
      {/* Primary on both halves: selecting Full-time used to switch this button to `secondary`,
          the burgundy reserved for app chrome, so the same control had two different "selected"
          colours. Rarely seen while Full-time could never be selected — obvious now that the
          employment-history seed pre-selects it. */}
      <Button
        variant={empType === "Full-time" ? "contained" : "outlined"}
        size="small"
        disabled={disabled}
        onClick={() => onChange("Full-time")}
      >
        Full-time
      </Button>
    </Box>
  );
};

export default PifSectionPosition;
